<template>
  <div class="wecom-chat" :class="{ exited }">
    <a-spin v-if="loading" class="state" />
    <div v-else-if="errorText" class="state">
      <span>{{ errorText }}</span>
      <a @click="queryInitial">重新加载</a>
    </div>
    <div v-else-if="initialized && !joined" class="state empty-state">
      <img class="empty-image" :src="weChatEmpty" alt="" />
      <span class="empty-text">患者暂未加入医助微信群</span>
    </div>
    <template v-else-if="joined">
      <div v-if="newMsgCount" class="new_mgs_pop" @click="handleNewMsgTips">
        <auth-img :src="moreMsg" />
        {{ newMsgCount > 99 ? '99+' : newMsgCount }}条新消息
      </div>
      <div ref="scrollRef" class="message-scroll" @scroll="onScroll">
        <div class="more-message">
          <div v-if="hasMore" class="getMoreMsg">
            <auth-img :src="getMoreMsg" alt="" @click="loadMore" />
            <a @click="loadMore">{{ loadingMore ? '加载中...' : '向上加载' }}</a>
            <a-input
              v-model:value="pageSize"
              style="width: 45px; border-radius: 4px"
              size="small"
            />
            <span>条</span>
          </div>
          <div class="getMoreMsg rightAction">
            <a v-if="!atTop" @click="scrollTop">
              <auth-img :src="toTop" alt="" />
              回到顶部
            </a>
            <a v-if="!atBottom" @click="scrollBottom">
              <auth-img :src="toBottom" alt="" />
              返回底部
            </a>
          </div>
        </div>

        <a-empty v-if="!messages.length" description="暂无企微消息" />
        <div
          v-for="item in messages"
          :key="item.msgId"
          class="message-item"
          :class="{ self: !isPatientMessage(item) }"
        >
          <auth-img
            class="avatar"
            :src="item.sendAvatar || getAvatarByRole(item.sendRole, item.memberType)"
          />
          <div class="message-body">
            <div class="sender">{{ item.sendName }} · {{ formatMsgTime(item.sendTime) }}</div>
            <div class="bubble">
              <WeComMessageContent :message="item" @preview-image="handlePreviewImage" />
            </div>
            <span v-if="item.status === 'sending'" class="sending">发送中...</span>
            <span v-else-if="item.status === 'failed'" class="sending failed">发送失败</span>
          </div>
        </div>
      </div>

      <WechatChatFooter
        v-if="!readonly"
        :quoteRequest="quoteRequest"
        :isManualIntervention="isManualIntervention"
        :takeoverLoading="takeoverLoading"
        :sendCompletedId="sendCompletedId"
        :sendFailedId="sendFailedId"
        :exited="exited"
        :exitDetail="exitDetail"
        @toggle-manual-intervention="toggleManualIntervention"
        @send-text="sendText"
        @send-image="sendImage"
        @select-common-word="emit('select-common-word', $event)"
      />
      <ImageViewer
        :visible="imageViewerVisible"
        :image-list="imagePreviewList"
        :current-id="previewImgId"
        @setVisible="setImageViewerVisible"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue'
import getMoreMsg from '@/assets/img/chat/more.png'
import toTop from '@/assets/img/chat/moreMessage/top.png'
import toBottom from '@/assets/img/chat/moreMessage/down.png'
import moreMsg from '@/assets/img/chat/more_msg.png'
import weChatEmpty from '@/assets/img/chat/weChatEmpty.png'
import WechatChatFooter from './WechatChatFooter.vue'
import WeComMessageContent from '../EnterpriseWeChatRecord/WeComMessageContent.vue'
import ImageViewer from '@/views/WorkPlace/Container/chat/components/ImageViewer/index.vue'
import {
  formatMsgTime,
  getAvatarByRole,
  getItemMediaUrl,
  isPreviewableImage
} from '../EnterpriseWeChatRecord/utils'
import useWechatPatientChat from './useWechatPatientChat'

const props = defineProps({
  active: { type: Boolean, default: false },
  doctorId: { type: [String, Number], default: '' },
  patientId: { type: [String, Number], default: '' },
  workOrderId: { type: [String, Number], default: '' },
  eligibility: {
    type: Object,
    default: () => ({ inGroup: false, quitGroupFlag: false })
  },
  quoteRequest: {
    type: Object,
    default: () => ({ id: 0, text: '' })
  },
  readonly: { type: Boolean, default: false },
  polling: { type: Boolean, default: true }
})
const emit = defineEmits(['joined-change', 'select-common-word'])
const isPatientMessage = item =>
  Number(item?.memberType) === 1 || ['PATIENT', '患者'].includes(item?.sendRole)
const {
  messages,
  loading,
  loadingMore,
  joined,
  initialized,
  exited,
  exitDetail,
  hasMore,
  pageSize,
  errorText,
  scrollRef,
  atTop,
  atBottom,
  newMsgCount,
  isManualIntervention,
  takeoverLoading,
  sendCompletedId,
  sendFailedId,
  toggleManualIntervention,
  queryInitial,
  loadMore,
  sendText,
  sendImage,
  onScroll,
  scrollTop,
  scrollBottom,
  handleNewMsgTips
} = useWechatPatientChat(props, emit)

const previewImgId = ref('')
const imageViewerVisible = ref(false)
const imagePreviewList = computed(() =>
  messages.value.filter(isPreviewableImage).map(item => ({
    url: getItemMediaUrl(item),
    msgId: item.msgId
  }))
)

const handlePreviewImage = item => {
  previewImgId.value = item.msgId
  imageViewerVisible.value = true
}

const setImageViewerVisible = status => {
  imageViewerVisible.value = status
  if (!status) previewImgId.value = ''
}
</script>

<style scoped lang="scss">
.wecom-chat {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #fafafa;
}

.wecom-chat.exited .new_mgs_pop {
  bottom: 262px;
}

.new_mgs_pop {
  position: absolute;
  right: 20px;
  bottom: 220px;
  z-index: 20;
  display: flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 6px 0 #0000001a;
  color: #24bca2;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    margin-right: 3px;
  }
}
.state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.empty-state {
  flex-direction: column;
  gap: 12px;

  .empty-image {
    width: 113px;
    height: 110px;
    object-fit: contain;
  }

  .empty-text {
    color: #999;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
  }
}
.message-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 20px;
}

.more-message {
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 16px 0;
  background-color: #fafafa;

  img {
    width: 12px;
    height: 12px;
    margin-right: -2px;
  }

  a {
    color: #014ea8;
    font-size: 12px;
    cursor: pointer;
  }

  span {
    color: #333;
    font-size: 12px;
  }

  .getMoreMsg {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;

    input {
      text-align: center;
    }
  }

  .rightAction {
    position: absolute;
    right: 0;
    gap: 20px;

    a img {
      margin: -2px 2px 0 0;
    }
  }
}
.message-item {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}
.message-item.self {
  flex-direction: row-reverse;
}
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex: none;
}
.message-body {
  max-width: 70%;
}
.self .message-body {
  text-align: right;
}
.sender {
  margin-bottom: 5px;
  color: #999;
  font-size: 12px;
}
.bubble {
  display: inline-block;
  max-width: 100%;
  padding: 10px 12px;
  border-radius: 0 8px 8px;
  background: #fff;
  text-align: left;
  word-break: break-all;
}
.self .bubble {
  border-radius: 8px 0 8px 8px;
  background: #dff3ff;
}
.sending {
  display: block;
  margin-top: 3px;
  color: #999;
  font-size: 12px;
}
.sending.failed {
  color: #ff4d4f;
}
</style>
